<!--
 * @Author: LiZhiWei
 * @Date: 2026-01-09 15:47:55
 * @LastEditors: LiZhiWei
 * @LastEditTime: 2026-08-17 16:41:43
 * @Description: 
-->
<template>
  <div class="home-page">
    <template v-if="homepage">
      <lazy-home-hero :banners="homepage.banners" />
      <lazy-home-news :news="homepage.news" />
      <lazy-home-overview />
      <lazy-home-capability />
      <lazy-home-products :items="homepage.products.items" />
      <lazy-home-study :items="homepage.study" />
      <lazy-home-organizations />
      <lazy-home-video :video="homepage.video" />
      <lazy-home-consultation />
    </template>
    <div v-else-if="pending" class="home-state" role="status">正在加载首页内容...</div>
    <div v-else class="home-state is-error" role="alert">
      <p>首页内容暂时无法加载，请稍后重试。</p>
      <button type="button" @click="refresh()">重新加载</button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { HomepageData } from '~/types/site-api'

  import {
    createOrganizationJsonLd,
    createSiteUrl,
    getPageSeo,
    getSiteUrl,
    siteSeo,
  } from '~/utils/seo'

  definePageMeta({ layout: 'default' })

  // 首页是静态爬虫入口；媒体卡片直接播放，详情路由须从公开 sitemap 目录补入。
  if (import.meta.prerender) {
    const urls = await $fetch<Array<string | { loc: string }>>('/__sitemap__/urls')
    prerenderRoutes(urls.map((url) => (typeof url === 'string' ? url : url.loc)))
  }

  const { getHomepage } = useSiteApi()
  const {
    data: homepageResponse,
    pending,
    refresh,
  } = await useAsyncData('site-homepage', getHomepage, {
    default: () => ({ data: null as HomepageData | null }),
  })
  const homepage = computed(() => homepageResponse.value?.data ?? null)

  const seo = getPageSeo('index')
  const siteUrl = getSiteUrl()

  useSeoMeta({
    title: seo.title,
    description: seo.description,
    ogTitle: seo.title,
    ogDescription: seo.description,
    ogType: 'website',
    ogImage: siteSeo.ogImage,
    twitterTitle: seo.title,
    twitterDescription: seo.description,
    twitterImage: siteSeo.ogImage,
  })

  useHead({
    meta: [{ name: 'keywords', content: seo.keywords?.join(', ') }],
    link: [{ rel: 'canonical', href: createSiteUrl('/', siteUrl) }],
  })
  useJsonLd(createOrganizationJsonLd(siteUrl))
</script>
<style scoped lang="scss">
  .home-page {
    min-width: 0;
    overflow: hidden;
    text-align: left;
  }

  .home-state {
    display: grid;
    place-items: center;
    min-height: 460px;
    padding: 32px 24px;
    color: var(--muted);
    text-align: center;
    background: var(--surface-soft);
  }

  .home-state p {
    margin: 0;
  }

  .home-state.is-error {
    gap: 14px;
  }

  .home-state button {
    padding: 0;
    color: var(--accent);
    cursor: pointer;
    background: transparent;
    border: 0;
  }

  :deep(.more-link-icon-mobile) {
    display: none !important;
    width: 16px;
    height: 16px;
  }

  :deep(.home-scroll-reveal.is-reveal-ready:not(.is-revealed) > .page-container) {
    opacity: 0;
    transform: translateY(30px);
  }

  :deep(.home-scroll-reveal.is-reveal-ready.is-revealed > .page-container) {
    opacity: 1;
    transform: none;
    transition:
      opacity 680ms var(--ease) 60ms,
      transform 680ms var(--ease) 60ms;
  }

  @media (max-width: 767px) {
    :deep(.home-scroll-reveal.is-reveal-ready:not(.is-revealed) > .page-container) {
      transform: translateY(20px);
    }

    :deep(.home-scroll-reveal.is-reveal-ready.is-revealed > .page-container) {
      transition:
        opacity 560ms var(--ease) 40ms,
        transform 560ms var(--ease) 40ms;
    }

    :deep(.section-more),
    :deep(.video-more) {
      gap: 4px;
    }

    :deep(.more-link-icon-desktop) {
      display: none !important;
    }

    :deep(.more-link-icon-mobile) {
      display: inline-block !important;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    :deep(.home-scroll-reveal > .page-container) {
      opacity: 1 !important;
      transform: none !important;
      transition: none !important;
    }
  }
</style>
